import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { PushDevice, PushPreferences, PushSubscriptionPayload, PushTestResult } from './push.types';

@Injectable({ providedIn: 'root' })
export class PushHttpService {
    private readonly http = inject(HttpClient);

    private readonly URL = `${environment.api_base}/push`;
    private readonly URL_VAPID = `${this.URL}/vapid-public-key`;
    private readonly URL_SUBSCRIPTIONS = `${this.URL}/subscriptions`;
    private readonly URL_PREFERENCES = `${this.URL}/preferences`;
    private readonly URL_TEST = `${this.URL}/test`;

    /** 200 `{ public_key }` · 503 when the backend has no VAPID keys. */
    reqVapidPublicKey() {
        return this.http.get<{ public_key: string }>(this.URL_VAPID);
    }

    reqDevices() {
        return this.http.get<PushDevice[]>(this.URL_SUBSCRIPTIONS);
    }

    /** Upsert by endpoint: 201 new · 200 refreshed. */
    reqRegister(payload: PushSubscriptionPayload) {
        return this.http.post<PushDevice>(this.URL_SUBSCRIPTIONS, payload);
    }

    reqUnregister(endpoint: string) {
        return this.http.delete<void>(this.URL_SUBSCRIPTIONS, { body: { endpoint } });
    }

    reqRemoveDevice(id: PushDevice['id']) {
        return this.http.delete<void>(`${this.URL_SUBSCRIPTIONS}/${id}`);
    }

    /** Sends a test notification to every device of the current user. */
    reqSendTest() {
        return this.http.post<PushTestResult>(this.URL_TEST, {});
    }

    reqPreferences() {
        return this.http.get<PushPreferences>(this.URL_PREFERENCES);
    }

    /** Partial bodies are allowed — the backend fills defaults and echoes the effective prefs. */
    reqSavePreferences(prefs: Partial<PushPreferences>) {
        return this.http.put<PushPreferences>(this.URL_PREFERENCES, prefs);
    }
}
